import { PhoneInput } from '../../../packages/account/src/PhoneInput'
import { normalizeParkerPhone } from '../../../packages/account/src/parkerPhone'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { api, type NoticeView, type PriceBreakdown } from './api'
import { getStripe, stripeConfigured } from './stripe'
import StripeModeGuard from './StripeModeGuard'

type Step = 'lookup' | 'detail' | 'account' | 'pay' | 'success'

// Stripe key handling (incl. the VITE_STRIPE_PUBLISHABLE_KEY / legacy
// VITE_STRIPE_PUBLIC_KEY fallback) lives in ./stripe; the promise is memoized there.
const stripePromise = getStripe()

// USD with thousands separators + fixed cents, so a notice never renders
// "$1234.00". (A shared formatter package is tracked as a separate later task.)
const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const fmt = (c: number) => usd.format(c / 100)

export default function App() {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const initialPlate = params.get('plate') ?? ''
  const initialNotice = params.get('notice') ?? ''

  const [step, setStep] = useState<Step>('lookup')
  const [error, setError] = useState<string | null>(null)
  const [notices, setNotices] = useState<NoticeView[]>([])
  const [active, setActive] = useState<NoticeView | null>(null)
  const [price, setPrice] = useState<PriceBreakdown | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [parker, setParker] = useState<{ email: string; phone: string; firstName: string; lastName: string; optIn: boolean }>({
    email: '', phone: '', firstName: '', lastName: '', optIn: false,
  })

  // Single in-flight flag for every async step. Disables the action buttons and
  // flips their labels to "Working…" so a step can't be re-fired mid-request —
  // most importantly, so a double-tap on "Continue to pay" can't create two
  // PaymentIntents (= two charges). startPayment also takes a ref-backed hard
  // guard below; the ref flips synchronously, before React re-renders.
  const [pending, setPending] = useState(false)
  const submittingRef = useRef(false)

  // Auto-lookup on mount if URL has plate or notice
  useEffect(() => {
    if (initialNotice || initialPlate) {
      void doLookup({ plate: initialPlate || undefined, notice: initialNotice || undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function doLookup(q: { plate?: string; notice?: string }) {
    if (pending) return
    setError(null)
    setPending(true)
    try {
      const out = await api.lookup(q)
      if (out.notices.length === 0) {
        setError('No active notices found. Double-check your plate or notice number.')
        return
      }
      if (out.notices.length === 1) {
        await loadDetail(out.notices[0].id)
      } else {
        setNotices(out.notices)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lookup failed')
    } finally {
      setPending(false)
    }
  }

  async function loadDetail(id: string) {
    setError(null)
    setPending(true)
    try {
      const out = await api.detail(id)
      setActive(out.notice)
      setPrice(out.price)
      setStep('detail')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load notice')
    } finally {
      setPending(false)
    }
  }

  async function applyPromo(code: string) {
    if (!active || pending) return
    setError(null)
    setPending(true)
    try {
      const out = await api.applyPromo(active.id, code)
      setPrice(out.price)
      if (!out.valid) setError('Code not valid for this notice')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Promo failed')
    } finally {
      setPending(false)
    }
  }

  async function startPayment() {
    if (!active || !price) return
    // Hard guard against a double-tap creating two PaymentIntents (two charges).
    // The ref flips synchronously, so even two taps in the same tick — before
    // React re-renders the button to its disabled state — can't both get past.
    if (pending || submittingRef.current) return
    submittingRef.current = true
    setPending(true)
    setError(null)
    try {
      // Save parker info first if anything was provided
      if (parker.email || parker.phone) {
        await api.upsertParker(active.id, {
          email: parker.email || undefined,
          phone: parker.phone || undefined,
          firstName: parker.firstName || undefined,
          lastName: parker.lastName || undefined,
          optIn: parker.optIn,
        })
      }
      const out = await api.createIntent(active.id, { promo: price.promo_code ?? undefined })
      setClientSecret(out.client_secret)
      setStep('pay')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start payment')
    } finally {
      submittingRef.current = false
      setPending(false)
    }
  }

  return (
    <div className="app">
      <Header />
      <div className="viewport">
        <Steps step={step} />
        {error && <div className="error">{error}</div>}

        {step === 'lookup' && (
          <Lookup
            initialPlate={initialPlate}
            initialNotice={initialNotice}
            notices={notices}
            pending={pending}
            onLookup={doLookup}
            onPick={loadDetail}
          />
        )}

        {step === 'detail' && active && price && (
          <Detail
            notice={active}
            price={price}
            pending={pending}
            onPromo={applyPromo}
            onContinue={() => setStep('account')}
          />
        )}

        {step === 'account' && active && price && (
          <Account
            parker={parker}
            setParker={setParker}
            price={price}
            pending={pending}
            onBack={() => setStep('detail')}
            onContinue={startPayment}
          />
        )}

        {step === 'pay' && clientSecret && stripeConfigured() && active && price && (
          <StripeModeGuard>
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
              <PayForm
                notice={active}
                price={price}
                onBack={() => setStep('account')}
                onSucceeded={() => setStep('success')}
              />
            </Elements>
          </StripeModeGuard>
        )}

        {step === 'pay' && !stripeConfigured() && (
          <div className="card">
            <div className="error">
              We couldn’t load the payment form. Please refresh and try again — if it keeps
              happening, email <a href="mailto:help@parkify.com">help@parkify.com</a> and we’ll
              help you take care of your notice.
            </div>
          </div>
        )}

        {step === 'success' && active && price && (
          <Success notice={active} price={price} parkerEmail={parker.email} parkerPhone={parker.phone} />
        )}
      </div>
      <Footer />
    </div>
  )
}

// ─── Header / Steps / Footer ──────────────────────────────────────────────

function Header() {
  return (
    <div className="header">
      <div>
        <div className="brand">parkify</div>
        <div className="tagline">Pay your parking notice</div>
      </div>
      <a href="mailto:appeals@parkify.com" className="tiny" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>
        Contest →
      </a>
    </div>
  )
}

function Steps({ step }: { step: Step }) {
  const order: Step[] = ['lookup', 'detail', 'account', 'pay']
  const idx = step === 'success' ? order.length : order.indexOf(step)
  return (
    <div className="steps">
      {order.map((_, i) => (
        <div key={i} className={`step-dot ${i < idx ? 'done' : i === idx ? 'active' : ''}`} />
      ))}
    </div>
  )
}

function Footer() {
  return (
    <div className="footer">
      Parking that works for you. ·{' '}
      <a href="mailto:appeals@parkify.com">appeals@parkify.com</a>
    </div>
  )
}

// ─── Step 1: Lookup ───────────────────────────────────────────────────────

function Lookup({
  initialPlate, initialNotice, notices, pending, onLookup, onPick,
}: {
  initialPlate: string
  initialNotice: string
  notices: NoticeView[]
  pending: boolean
  onLookup: (q: { plate?: string; notice?: string }) => void
  onPick: (id: string) => void
}) {
  const [mode, setMode] = useState<'plate' | 'notice'>(initialNotice ? 'notice' : 'plate')
  const [plate, setPlate] = useState(initialPlate)
  const [noticeId, setNoticeId] = useState(initialNotice)

  if (notices.length > 1) {
    return (
      <div className="card">
        <h1 className="h1">Choose a notice</h1>
        <p className="muted">We found {notices.length} unpaid notices for that plate.</p>
        {notices.map(n => (
          <button key={n.id} className="notice-pick" onClick={() => onPick(n.id)}>
            <div className="top">
              <span className="id">{n.id}</span>
              <span className="amt">{fmt(n.total_cents)}</span>
            </div>
            <div className="bot">
              {n.property_name} · {new Date(n.observed_at).toLocaleDateString()}
            </div>
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="card">
      <h1 className="h1">Look up your notice</h1>
      <p className="muted">Enter your license plate or notice number.</p>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={mode === 'plate'} className="tab" onClick={() => setMode('plate')}>License plate</button>
        <button role="tab" aria-selected={mode === 'notice'} className="tab" onClick={() => setMode('notice')}>Notice #</button>
      </div>
      {mode === 'plate' ? (
        <input
          className="input" placeholder="ABC1234"
          value={plate} onChange={e => setPlate(e.target.value.toUpperCase())}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter' && plate.trim() && !pending) onLookup({ plate: plate.trim() }) }}
        />
      ) : (
        <input
          className="input" placeholder="PKF-2026-XXXXXX"
          value={noticeId} onChange={e => setNoticeId(e.target.value.toUpperCase())}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter' && noticeId.trim() && !pending) onLookup({ notice: noticeId.trim() }) }}
        />
      )}
      <div style={{ height: 16 }} />
      <button
        className="btn"
        disabled={pending || (mode === 'plate' ? !plate.trim() : !noticeId.trim())}
        onClick={() => onLookup(mode === 'plate' ? { plate: plate.trim() } : { notice: noticeId.trim() })}
      >
        {pending ? 'Working…' : 'Find my notice'}
      </button>
    </div>
  )
}

// ─── Step 2: Detail ───────────────────────────────────────────────────────

function Detail({
  notice, price, pending, onPromo, onContinue,
}: {
  notice: NoticeView
  price: PriceBreakdown
  pending: boolean
  onPromo: (code: string) => void
  onContinue: () => void
}) {
  const [showDetails, setShowDetails] = useState(false)
  const [promoOpen, setPromoOpen] = useState(false)
  const [promo, setPromo] = useState(price.promo_code ?? '')

  return (
    <div className="card">
      <h1 className="h1">{fmt(price.total_cents)}</h1>
      <p className="muted">Total amount due</p>
      <div className="summary">
        <div className="id">{notice.id}</div>
        <div className="property">{notice.property_name}</div>
        <div className="meta">
          {notice.violation_label} · {new Date(notice.observed_at).toLocaleDateString()}
        </div>
        <div className="meta">Plate {notice.plate}{notice.plate_state ? ` (${notice.plate_state})` : ''}</div>
      </div>

      <button className="detail-toggle" onClick={() => setShowDetails(s => !s)}>
        {showDetails ? '▾ Hide details' : '▸ Show details'}
      </button>
      {showDetails && (
        <div>
          <div className="row"><span className="label">Base fine</span><span className="value">{fmt(price.base_cents)}</span></div>
          <div className="row"><span className="label">Administration fee</span><span className="value">{fmt(price.admin_fee_cents)}</span></div>
          <div className="row"><span className="label">Tax</span><span className="value">{fmt(price.tax_cents)}</span></div>
          {price.promo_discount_cents > 0 && (
            <div className="row"><span className="label">Promo {price.promo_code}</span><span className="value" style={{ color: 'var(--success)' }}>−{fmt(price.promo_discount_cents)}</span></div>
          )}
          <div className="row total divider"><span className="label">Total</span><span className="value">{fmt(price.total_cents)}</span></div>
        </div>
      )}

      <div style={{ height: 8 }} />
      {!promoOpen ? (
        <button className="detail-toggle" onClick={() => setPromoOpen(true)}>+ Promo or validation code</button>
      ) : (
        <div className="form-row">
          <label>Promo code</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input normal" value={promo} onChange={e => setPromo(e.target.value)} placeholder="VALID-CODE" />
            <button
              className="btn secondary" style={{ width: 100, height: 56 }}
              disabled={pending || !promo.trim()}
              onClick={() => onPromo(promo)}
            >
              {pending ? '…' : 'Apply'}
            </button>
          </div>
        </div>
      )}

      <div style={{ height: 16 }} />
      <button className="btn" disabled={pending} onClick={onContinue}>
        {pending ? 'Working…' : 'Continue'}
      </button>
    </div>
  )
}

// ─── Step 3: Account ──────────────────────────────────────────────────────

// Email/phone are optional (guest checkout is fine), but if a parker types
// something it has to be usable — we won't tell them we'll send a receipt to a
// malformed address/number. Forgiving by design: a blank field is valid.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const emailValid = (v: string) => EMAIL_RE.test(v.trim())
const phoneValid = (v: string) => normalizeParkerPhone(v) !== null

function Account({
  parker, setParker, price, pending, onBack, onContinue,
}: {
  parker: { email: string; phone: string; firstName: string; lastName: string; optIn: boolean }
  setParker: (p: typeof parker) => void
  price: PriceBreakdown
  pending: boolean
  onBack: () => void
  onContinue: () => void
}) {
  const [touched, setTouched] = useState<{ email: boolean; phone: boolean }>({ email: false, phone: false })
  const emailBad = parker.email.trim() !== '' && !emailValid(parker.email)
  const phoneBad = parker.phone.trim() !== '' && !phoneValid(parker.phone)
  const canContinue = !emailBad && !phoneBad

  return (
    <div className="card">
      <h2 className="h2">Save info for next time?</h2>
      <p className="muted">Add your email or phone to get a receipt. Optional.</p>
      <div className="form-row">
        <label>Email</label>
        <input className="input normal" type="email" value={parker.email}
          aria-invalid={emailBad && touched.email}
          onBlur={() => setTouched(t => ({ ...t, email: true }))}
          onChange={e => setParker({ ...parker, email: e.target.value })} placeholder="you@example.com" />
        {emailBad && touched.email && <span className="field-error">Enter a valid email, or leave it blank.</span>}
      </div>
      <div className="form-row">
        <label>Phone</label>
        <PhoneInput className="input normal" overflow="keep" value={parker.phone}
          aria-invalid={phoneBad && touched.phone}
          onBlur={() => setTouched(t => ({ ...t, phone: true }))}
          onChange={value => setParker({ ...parker, phone: value })} placeholder="(555) 123-4567" />
        {phoneBad && touched.phone && <span className="field-error">Enter a valid phone number, or leave it blank.</span>}
      </div>
      <div className="checkbox">
        <input id="optin" type="checkbox" checked={parker.optIn}
          onChange={e => setParker({ ...parker, optIn: e.target.checked })} />
        <label htmlFor="optin">
          Create a Parkify account so my plate is recognized at any Parkify location.
          I'll only ever be charged after explicitly confirming a session.
        </label>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button className="btn secondary" style={{ flex: 1 }} disabled={pending} onClick={onBack}>Back</button>
        <button className="btn" style={{ flex: 2 }} disabled={pending || !canContinue} onClick={onContinue}>
          {pending ? 'Working…' : `Continue to pay ${fmt(price.total_cents)}`}
        </button>
      </div>
    </div>
  )
}

// ─── Step 4: Pay ──────────────────────────────────────────────────────────

function PayForm({
  notice, price, onBack, onSucceeded,
}: {
  notice: NoticeView
  price: PriceBreakdown
  onBack: () => void
  onSucceeded: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setBusy(true); setErr(null)
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    })
    setBusy(false)
    if (error) { setErr(error.message ?? 'Payment failed'); return }
    // Treat `processing` like `succeeded`: wallet / bank-backed methods (Apple
    // Pay, Google Pay, some cards) settle asynchronously, so confirmPayment
    // returns `processing`, not `succeeded`. The webhook (+ reconciliation)
    // finalizes the notice once it settles. Mirrors the consumer PaymentStep —
    // advancing here avoids a silent dead-end that invites a double-payment.
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      onSucceeded()
      return
    }
    // Any other terminal status — surface a generic retry rather than hang.
    setErr('Payment didn’t complete. Please try again.')
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="h2">Confirm payment</h2>
      <p className="muted">Notice {notice.id} · {fmt(price.total_cents)}</p>
      <PaymentElement />
      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button type="button" className="btn secondary" style={{ flex: 1 }} onClick={onBack} disabled={busy}>Back</button>
        <button type="submit" className="btn" style={{ flex: 2 }} disabled={!stripe || busy}>
          {busy ? 'Processing…' : `Pay ${fmt(price.total_cents)}`}
        </button>
      </div>
    </form>
  )
}

// ─── Step 5: Success ──────────────────────────────────────────────────────

function Success({
  notice, price, parkerEmail, parkerPhone,
}: {
  notice: NoticeView
  price: PriceBreakdown
  parkerEmail: string
  parkerPhone: string
}) {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <div className="success-icon">✓</div>
      <h1 className="h1">Payment received</h1>
      <p className="muted">Notice {notice.id} · {fmt(price.total_cents)}</p>
      <div className="summary" style={{ textAlign: 'left' }}>
        <div className="property">{notice.property_name}</div>
        <div className="meta">Plate {notice.plate} · paid {new Date().toLocaleString()}</div>
        {parkerEmail && <div className="meta">We'll send a receipt to {parkerEmail}</div>}
        {parkerPhone && <div className="meta">We'll text a confirmation to {parkerPhone}</div>}
      </div>
      <p className="tiny" style={{ marginTop: 16 }}>Your account is in good standing. You can close this page.</p>
    </div>
  )
}
